/**
 * Gift Message Manager Component
 * Handles personalized gift messages for cart line items
 * Uses localStorage for draft persistence and explicit save to Shopify
 * 
 * Simplified UI: Single "Save All" button at top, Clear buttons per message
 */

class GiftMessageManager extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.maxChars = parseInt(this.dataset.maxChars) || 280;
    this.lineItemKey = this.dataset.lineItemKey;
    this.lineIndex = parseInt(this.dataset.lineIndex) || 1;
    this.quantity = parseInt(this.dataset.quantity) || 1;
    this.maxQuantity = parseInt(this.dataset.maxQuantity) || 10;

    // Allowed characters regex (letters, numbers, basic punctuation, spaces, newlines)
    this.allowedCharsRegex = /^[A-Za-z0-9 .,!?'"\-:;()&@#$%+=\n]*$/;

    // State
    this.messages = {};        // Current draft messages (from localStorage)
    this.savedMessages = {};   // Last saved messages (from Shopify)
    this.isSaving = false;

    // LocalStorage key
    this.storageKey = `gift_messages_${this.lineItemKey}`;

    // Parse existing saved messages from Shopify
    this.parseSavedMessages();

    // Load draft from localStorage or use saved messages
    this.loadDraft();

    // Bind methods
    this.handleInput = this.handleInput.bind(this);
    this.handleClearMessage = this.handleClearMessage.bind(this);
    this.handleSaveAll = this.handleSaveAll.bind(this);
  }

  connectedCallback() {
    this.render();
    
    // Only attach event listeners once (they use event delegation so they persist across re-renders)
    if (!this._listenersAttached) {
      this.attachEventListeners();
      this._listenersAttached = true;
    }

    // Subscribe to cart updates for quantity changes
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'gift-message') return;
        this.handleCartUpdate(event);
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  parseSavedMessages() {
    try {
      const existingData = this.dataset.existingMessages;
      if (existingData) {
        const properties = JSON.parse(existingData);
        Object.keys(properties).forEach(key => {
          const match = key.match(/^Gift Message (\d+)$/);
          if (match) {
            const index = parseInt(match[1]);
            const value = properties[key];
            if (value && value.trim()) {
              this.savedMessages[index] = value;
            }
          }
        });
      }
    } catch (e) {
      console.error('Error parsing existing messages:', e);
    }
  }

  loadDraft() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          this.messages = parsed;
          return;
        }
      }
    } catch (e) {
      console.error('Error loading draft from localStorage:', e);
    }

    // Fall back to saved messages or create empty slots based on quantity
    if (Object.keys(this.savedMessages).length > 0) {
      this.messages = { ...this.savedMessages };
    } else {
      // Initialize with quantity number of empty slots
      this.messages = {};
      for (let i = 1; i <= this.quantity; i++) {
        this.messages[i] = '';
      }
    }
  }

  saveDraft() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.messages));
    } catch (e) {
      console.error('Error saving draft to localStorage:', e);
    }
  }

  clearDraft() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.error('Error clearing draft from localStorage:', e);
    }
  }

  hasUnsavedChanges(index) {
    const draft = (this.messages[index] || '').trim();
    const saved = (this.savedMessages[index] || '').trim();
    return draft !== saved;
  }

  hasAnyUnsavedChanges() {
    // Check all slots up to quantity
    for (let i = 1; i <= this.quantity; i++) {
      if (this.hasUnsavedChanges(i)) {
        return true;
      }
    }
    return false;
  }

  render() {
    const hasUnsaved = this.hasAnyUnsavedChanges();

    this.innerHTML = `
      <div class="gift-message-container">
        <div class="gift-message-header">
          <div class="gift-message-header-left">
            <span class="gift-icon" aria-hidden="true">&#127873;</span>
            <h3>${window.giftMessageStrings?.title || 'Personalize Your Gift Boxes'}</h3>
          </div>
          <div class="gift-message-header-right">
            <span class="gift-message-global-status"></span>
            <button 
              type="button" 
              class="gift-message-save-all-btn"
              ${!hasUnsaved ? 'disabled' : ''}
              aria-label="${window.giftMessageStrings?.save_all || 'Save All Messages'}"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              ${window.giftMessageStrings?.save_all || 'Save All'}
            </button>
          </div>
        </div>

        <div class="gift-message-list">
          ${this.renderMessages()}
        </div>

        <p class="gift-message-info">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          ${window.giftMessageStrings?.info || 'Letters, numbers, and basic punctuation only. No emojis or special symbols.'}
        </p>
      </div>
    `;
  }

  renderMessages() {
    const slots = [];

    // Always show quantity number of message slots
    for (let i = 1; i <= this.quantity; i++) {
      const message = this.messages[i] || '';
      const charCount = message.length;
      const isNearLimit = charCount >= this.maxChars * 0.9;
      const isAtLimit = charCount >= this.maxChars;
      const hasUnsaved = this.hasUnsavedChanges(i);
      const hasContent = message.trim().length > 0;

      slots.push(`
        <div class="gift-message-item ${hasUnsaved ? 'has-unsaved-changes' : ''}" data-message-index="${i}">
          <div class="gift-message-item-header">
            <label class="gift-message-label" for="gift-message-${this.lineItemKey}-${i}">
              ${window.giftMessageStrings?.label || 'Gift Message'} ${i}
              <span class="gift-message-label-optional">(${window.giftMessageStrings?.optional || 'Optional'})</span>
              ${hasUnsaved ? '<span class="gift-message-draft-badge">Draft</span>' : ''}
            </label>
          </div>

          <textarea
            id="gift-message-${this.lineItemKey}-${i}"
            class="gift-message-textarea ${hasUnsaved ? 'has-unsaved' : ''}"
            data-message-index="${i}"
            placeholder="${window.giftMessageStrings?.placeholder || 'Enter your personalized message...'}"
            maxlength="${this.maxChars}"
            rows="4"
          >${this.escapeHtml(message)}</textarea>

          <div class="gift-message-footer">
            <span class="gift-message-counter ${isNearLimit ? 'near-limit' : ''} ${isAtLimit ? 'at-limit' : ''}">
              ${charCount}/${this.maxChars} ${window.giftMessageStrings?.characters || 'characters'}
            </span>

            <button 
              type="button" 
              class="gift-message-clear-btn"
              data-clear-index="${i}"
              ${!hasContent ? 'disabled' : ''}
              aria-label="${window.giftMessageStrings?.clear || 'Clear message'}"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              ${window.giftMessageStrings?.clear || 'Clear'}
            </button>
          </div>

          <div class="gift-message-warning" data-warning-index="${i}" style="display: none;">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <span class="warning-text"></span>
          </div>
        </div>
      `);
    }

    return slots.join('');
  }

  attachEventListeners() {
    // Input handler for textareas (using event delegation on the component)
    this.addEventListener('input', (e) => {
      if (e.target.classList.contains('gift-message-textarea')) {
        this.handleInput(e);
      }
    });

    // Save All button - use event delegation to handle dynamically created buttons
    this.addEventListener('click', (e) => {
      // Handle Save All button
      const saveAllBtn = e.target.closest('.gift-message-save-all-btn');
      if (saveAllBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (!saveAllBtn.disabled) {
          this.handleSaveAll();
        }
        return;
      }
      
      // Handle Clear buttons
      const clearBtn = e.target.closest('.gift-message-clear-btn');
      if (clearBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (!clearBtn.disabled) {
          this.handleClearMessage(clearBtn.dataset.clearIndex);
        }
      }
    });
  }

  handleInput(event) {
    const textarea = event.target;
    const index = parseInt(textarea.dataset.messageIndex);
    let value = textarea.value;

    // Validate and sanitize input
    const { sanitized, hadInvalidChars } = this.sanitizeInput(value);

    if (hadInvalidChars) {
      const cursorPos = textarea.selectionStart;
      textarea.value = sanitized;
      const newPos = Math.min(cursorPos, sanitized.length);
      textarea.setSelectionRange(newPos, newPos);

      this.showWarning(index, window.giftMessageStrings?.warning_invalid ||
        'Some characters were removed. Only letters, numbers, spaces, and basic punctuation are allowed.');
    } else {
      this.hideWarning(index);
    }

    // Update state
    this.messages[index] = sanitized;

    // Save draft to localStorage
    this.saveDraft();

    // Update UI
    this.updateCharCounter(index, sanitized.length);
    this.updateUnsavedState(index);
    this.updateSaveAllButton();
  }

  sanitizeInput(value) {
    let sanitized = '';
    let hadInvalidChars = false;

    for (const char of value) {
      if (this.allowedCharsRegex.test(char)) {
        sanitized += char;
      } else {
        hadInvalidChars = true;
      }
    }

    if (sanitized.length > this.maxChars) {
      sanitized = sanitized.substring(0, this.maxChars);
    }

    return { sanitized, hadInvalidChars };
  }

  updateCharCounter(index, count) {
    const item = this.querySelector(`[data-message-index="${index}"]`);
    if (!item) return;

    const counter = item.querySelector('.gift-message-counter');
    if (counter) {
      const isNearLimit = count >= this.maxChars * 0.9;
      const isAtLimit = count >= this.maxChars;

      counter.textContent = `${count}/${this.maxChars} ${window.giftMessageStrings?.characters || 'characters'}`;
      counter.classList.toggle('near-limit', isNearLimit && !isAtLimit);
      counter.classList.toggle('at-limit', isAtLimit);
    }
  }

  updateUnsavedState(index) {
    const item = this.querySelector(`[data-message-index="${index}"]`);
    if (!item) return;

    const hasUnsaved = this.hasUnsavedChanges(index);
    const hasContent = (this.messages[index] || '').trim().length > 0;

    // Update item class
    item.classList.toggle('has-unsaved-changes', hasUnsaved);

    // Update textarea border
    const textarea = item.querySelector('.gift-message-textarea');
    if (textarea) {
      textarea.classList.toggle('has-unsaved', hasUnsaved);
    }

    // Update draft badge
    const label = item.querySelector('.gift-message-label');
    let badge = label.querySelector('.gift-message-draft-badge');
    if (hasUnsaved && !badge) {
      badge = document.createElement('span');
      badge.className = 'gift-message-draft-badge';
      badge.textContent = 'Draft';
      label.appendChild(badge);
    } else if (!hasUnsaved && badge) {
      badge.remove();
    }

    // Update clear button
    const clearBtn = item.querySelector('.gift-message-clear-btn');
    if (clearBtn) {
      clearBtn.disabled = !hasContent;
    }
  }

  updateSaveAllButton() {
    const saveAllBtn = this.querySelector('.gift-message-save-all-btn');
    if (saveAllBtn) {
      const hasUnsaved = this.hasAnyUnsavedChanges();
      saveAllBtn.disabled = !hasUnsaved;
    }
  }

  showWarning(index, message) {
    const warning = this.querySelector(`[data-warning-index="${index}"]`);
    if (warning) {
      warning.querySelector('.warning-text').textContent = message;
      warning.style.display = 'flex';

      setTimeout(() => this.hideWarning(index), 5000);
    }
  }

  hideWarning(index) {
    const warning = this.querySelector(`[data-warning-index="${index}"]`);
    if (warning) {
      warning.style.display = 'none';
    }
  }

  showGlobalStatus(type, message) {
    const container = this.querySelector('.gift-message-global-status');
    if (!container) return;

    const icons = {
      saving: `<svg class="status-icon spinning" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line></svg>`,
      saved: `<svg class="status-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>`,
      error: `<svg class="status-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`
    };

    container.innerHTML = `<span class="gift-message-status ${type}">${icons[type] || ''} ${message}</span>`;

    if (type === 'saved') {
      setTimeout(() => {
        container.innerHTML = '';
      }, 3000);
    }
  }

  handleClearMessage(index) {
    index = parseInt(index);

    // Clear the message
    this.messages[index] = '';

    // Save draft
    this.saveDraft();

    // Update the textarea
    const textarea = this.querySelector(`[data-message-index="${index}"] textarea`);
    if (textarea) {
      textarea.value = '';
    }

    // Update UI
    this.updateCharCounter(index, 0);
    this.updateUnsavedState(index);
    this.updateSaveAllButton();
  }

  buildProperties() {
    const properties = {};
    let messageNumber = 1;

    // Only include non-empty messages, reindex them
    for (let i = 1; i <= this.quantity; i++) {
      const message = this.messages[i];
      if (message && message.trim()) {
        properties[`Gift Message ${messageNumber}`] = message.trim();
        messageNumber++;
      }
    }

    // Clear any extra gift message properties (set to empty string to remove them)
    for (let i = messageNumber; i <= this.maxQuantity; i++) {
      properties[`Gift Message ${i}`] = '';
    }

    return properties;
  }

  async handleSaveAll() {
    if (this.isSaving) return;
    this.isSaving = true;

    const saveAllBtn = this.querySelector('.gift-message-save-all-btn');
    if (saveAllBtn) {
      saveAllBtn.disabled = true;
    }

    // Store current focus to restore later
    const activeElement = document.activeElement;
    const activeTextareaIndex = activeElement?.classList.contains('gift-message-textarea') 
      ? activeElement.dataset.messageIndex 
      : null;
    const cursorPosition = activeTextareaIndex ? activeElement.selectionStart : null;

    this.showGlobalStatus('saving', window.giftMessageStrings?.saving || 'Saving...');

    try {
      const properties = this.buildProperties();

      // IMPORTANT: Must include quantity to prevent Shopify from defaulting to 1
      // The /cart/change.json API requires quantity when updating properties
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: this.lineItemKey,
          quantity: this.quantity,  // Preserve the current quantity!
          properties: properties
        })
      });

      const data = await response.json();
      
      if (!response.ok || data.errors) {
        throw new Error(data.errors || data.message || 'Failed to save');
      }

      // Update lineItemKey from response since it changes when properties change
      const updatedItem = data.items ? data.items.find(item => 
        item.variant_id === data.items[this.lineIndex - 1]?.variant_id
      ) || data.items[this.lineIndex - 1] : null;
      
      if (updatedItem) {
        this.lineItemKey = updatedItem.key;
        this.dataset.lineItemKey = updatedItem.key;
        this.storageKey = `gift_messages_${this.lineItemKey}`;
      }

      // Update saved state and messages - keep non-empty messages
      this.savedMessages = {};
      const newMessages = {};
      for (let i = 1; i <= this.quantity; i++) {
        const message = this.messages[i];
        if (message && message.trim()) {
          this.savedMessages[i] = message.trim();
          newMessages[i] = message.trim();
        } else {
          newMessages[i] = '';
        }
      }
      this.messages = newMessages;

      // Clear the draft from localStorage since it's now saved
      this.clearDraft();

      // Update the data attribute with new existing messages for consistency
      this.dataset.existingMessages = JSON.stringify(
        Object.fromEntries(
          Object.entries(this.savedMessages).map(([idx, msg]) => [`Gift Message ${idx}`, msg])
        )
      );

      this.showGlobalStatus('saved', window.giftMessageStrings?.saved || 'Saved!');

      // Re-render to update all unsaved states
      this.render();

      // Restore focus if user was typing in a textarea
      if (activeTextareaIndex) {
        requestAnimationFrame(() => {
          const textarea = this.querySelector(`textarea[data-message-index="${activeTextareaIndex}"]`);
          if (textarea) {
            textarea.focus();
            if (cursorPosition !== null) {
              textarea.setSelectionRange(cursorPosition, cursorPosition);
            }
          }
        });
      }

      // Fetch fresh cart data before publishing to ensure accurate totals
      // The /cart/change.json response may not have complete cart totals
      let freshCartData = data;
      try {
        // Use routes object which is more reliably available
        const cartUrl = (window.Shopify?.routes?.root || window.routes?.root || '/') + 'cart.js';
        const freshCartResponse = await fetch(cartUrl);
        if (freshCartResponse.ok) {
          freshCartData = await freshCartResponse.json();
        }
      } catch (fetchError) {
        console.warn('Could not fetch fresh cart data, using change response:', fetchError);
      }

      // Publish cart update event so other components know about the change
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'gift-message', 
          cartData: freshCartData 
        });
      }

    } catch (error) {
      console.error('Error saving gift messages:', error);
      this.showGlobalStatus('error', window.giftMessageStrings?.save_error || 'Failed to save');

      const currentSaveBtn = this.querySelector('.gift-message-save-all-btn');
      if (currentSaveBtn) {
        currentSaveBtn.disabled = false;
      }
    } finally {
      this.isSaving = false;
    }
  }

  // Alias for pre-checkout handler compatibility
  handleSave() {
    return this.handleSaveAll();
  }

  handleCartUpdate(event) {
    // Don't process updates while we're saving (we'll handle the update ourselves)
    if (this.isSaving) return;
    
    if (!event.cartData || !event.cartData.items) return;

    // Try to find the item by key first, then by index as fallback
    let item = event.cartData.items.find(item => item.key === this.lineItemKey);
    
    // If not found by key (key can change after properties update), try by index
    if (!item && this.lineIndex > 0 && this.lineIndex <= event.cartData.items.length) {
      item = event.cartData.items[this.lineIndex - 1];
      // Update the key if we found the item by index
      if (item) {
        this.lineItemKey = item.key;
        this.dataset.lineItemKey = item.key;
        this.storageKey = `gift_messages_${this.lineItemKey}`;
      }
    }

    if (item) {
      const newQuantity = item.quantity;

      if (newQuantity !== this.quantity) {
        // Store current focus to restore later
        const activeElement = document.activeElement;
        const activeTextareaIndex = activeElement?.classList.contains('gift-message-textarea') 
          ? activeElement.dataset.messageIndex 
          : null;
        const cursorPosition = activeTextareaIndex ? activeElement.selectionStart : null;

        if (newQuantity < this.quantity) {
          this.handleQuantityDecrease(newQuantity);
        } else if (newQuantity > this.quantity) {
          this.handleQuantityIncrease(newQuantity);
        }

        this.quantity = newQuantity;
        this.dataset.quantity = newQuantity;
        this.render();

        // Restore focus if user was typing in a textarea
        if (activeTextareaIndex && parseInt(activeTextareaIndex) <= newQuantity) {
          const textarea = this.querySelector(`textarea[data-message-index="${activeTextareaIndex}"]`);
          if (textarea) {
            textarea.focus();
            if (cursorPosition !== null) {
              textarea.setSelectionRange(cursorPosition, cursorPosition);
            }
          }
        }
      }
    }
  }

  handleQuantityDecrease(newQuantity) {
    // Remove messages beyond the new quantity
    for (let i = newQuantity + 1; i <= this.quantity; i++) {
      delete this.messages[i];
    }
    this.saveDraft();
  }

  handleQuantityIncrease(newQuantity) {
    // Add empty slots for new quantity
    for (let i = this.quantity + 1; i <= newQuantity; i++) {
      if (!this.messages[i]) {
        this.messages[i] = '';
      }
    }
    this.saveDraft();
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('gift-message-manager', GiftMessageManager);

/**
 * Gift Messages Drawer Toggle (for cart drawer)
 */
class GiftMessagesDrawer extends HTMLElement {
  constructor() {
    super();
    this.toggle = null;
    this.content = null;
    this.lineItemKey = null;
    this.isDeleting = false;
  }

  connectedCallback() {
    this.toggle = this.querySelector('.gift-messages-drawer-toggle');
    this.content = this.querySelector('.gift-messages-drawer-content');
    this.lineItemKey = this.dataset.lineItemKey;

    if (this.toggle && this.content) {
      this.toggle.addEventListener('click', () => this.handleToggle());
    }

    this.attachDeleteListeners();
  }

  handleToggle() {
    const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', !isExpanded);
    this.content.classList.toggle('expanded', !isExpanded);
  }

  attachDeleteListeners() {
    const deleteButtons = this.querySelectorAll('.gift-messages-drawer-delete');
    deleteButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleDeleteMessage(e, button));
    });
  }

  async handleDeleteMessage(event, button) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isDeleting) return;
    this.isDeleting = true;

    const messageProperty = button.dataset.messageProperty;

    if (!messageProperty) {
      console.error('No message property found on delete button');
      this.isDeleting = false;
      return;
    }

    const itemElement = button.closest('.gift-messages-drawer-item');
    if (itemElement) {
      itemElement.classList.add('deleting');
    }

    try {
      // First fetch the current cart to get the line item's quantity
      const cartData = await this.getCartData();
      const lineItem = cartData.items.find(item => item.key === this.lineItemKey);
      
      if (!lineItem) {
        throw new Error('Line item not found in cart');
      }
      
      const currentProperties = lineItem.properties || {};
      const updatedProperties = this.buildUpdatedProperties(currentProperties, messageProperty);

      const sections = ['cart-drawer', 'cart-icon-bubble'];

      // IMPORTANT: Include quantity to prevent Shopify from defaulting to 1
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: this.lineItemKey,
          quantity: lineItem.quantity,  // Preserve the current quantity!
          properties: updatedProperties,
          sections: sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete message');
      }

      const data = await response.json();

      this.clearLocalStorageDraft(messageProperty);

      setTimeout(() => {
        this.refreshCartDrawer(data);
      }, 200);

    } catch (error) {
      console.error('Error deleting gift message:', error);

      if (itemElement) {
        itemElement.classList.remove('deleting');
      }
    } finally {
      this.isDeleting = false;
    }
  }

  clearLocalStorageDraft(messageProperty) {
    try {
      const storageKey = `gift_messages_${this.lineItemKey}`;
      const storedData = localStorage.getItem(storageKey);

      if (storedData) {
        const messages = JSON.parse(storedData);

        const match = messageProperty.match(/^Gift Message (\d+)$/);
        if (match) {
          const deleteIndex = parseInt(match[1]);

          if (messages[deleteIndex]) {
            delete messages[deleteIndex];

            // Reindex remaining messages
            const sortedIndices = Object.keys(messages).map(Number).sort((a, b) => a - b);
            const newMessages = {};

            sortedIndices.forEach((oldIndex, newIndex) => {
              newMessages[newIndex + 1] = messages[oldIndex];
            });

            if (Object.keys(newMessages).length === 0) {
              localStorage.removeItem(storageKey);
            } else {
              localStorage.setItem(storageKey, JSON.stringify(newMessages));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error clearing localStorage draft:', error);
    }
  }

  async getCartData() {
    try {
      const cartUrl = (window.Shopify?.routes?.root || window.routes?.root || '/') + 'cart.js';
      const response = await fetch(cartUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart data:', error);
      throw error;
    }
  }

  buildUpdatedProperties(currentProperties, deleteProperty) {
    const giftMessages = [];

    Object.keys(currentProperties).forEach(key => {
      const match = key.match(/^Gift Message (\d+)$/);
      if (match && key !== deleteProperty) {
        const index = parseInt(match[1]);
        const value = currentProperties[key];
        // Only keep non-empty messages
        if (value && value.trim()) {
          giftMessages.push({ index, key, value: value.trim() });
        }
      }
    });

    const sortedMessages = giftMessages.sort((a, b) => a.index - b.index);
    const newProperties = {};

    // Reindex messages
    sortedMessages.forEach((msg, i) => {
      const newIndex = i + 1;
      newProperties[`Gift Message ${newIndex}`] = msg.value;
    });

    // Preserve non-gift message properties
    Object.keys(currentProperties).forEach(key => {
      if (!key.match(/^Gift Message \d+$/)) {
        newProperties[key] = currentProperties[key];
      }
    });

    return newProperties;
  }

  refreshCartDrawer(data) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      console.error('Cart drawer element not found');
      return;
    }

    if (!data.sections || !data.sections['cart-drawer']) {
      console.error('No cart-drawer section in response data');
      return;
    }

    const html = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
    const lineItemKey = this.lineItemKey;

    const newGiftMessagesDrawer = html.querySelector(`[data-line-item-key="${lineItemKey}"]`);
    if (!newGiftMessagesDrawer) {
      // If no gift messages drawer found, the messages were all deleted - remove this element
      this.remove();
      return;
    }

    this.replaceWith(newGiftMessagesDrawer);
  }
}

customElements.define('gift-messages-drawer', GiftMessagesDrawer);

/**
 * Pre-checkout handler - saves all unsaved messages before checkout
 */
document.addEventListener('DOMContentLoaded', () => {
  const checkoutButtons = document.querySelectorAll('[name="checkout"], #checkout, #CartDrawer-Checkout');

  checkoutButtons.forEach(button => {
    button.addEventListener('click', async (e) => {
      const giftMessageManagers = document.querySelectorAll('gift-message-manager');

      // Check if any have unsaved changes
      let hasUnsaved = false;
      giftMessageManagers.forEach(manager => {
        if (manager.hasAnyUnsavedChanges && manager.hasAnyUnsavedChanges()) {
          hasUnsaved = true;
        }
      });

      if (hasUnsaved) {
        e.preventDefault();

        // Save all messages
        const savePromises = Array.from(giftMessageManagers).map(manager => {
          return manager.handleSaveAll();
        });

        try {
          await Promise.all(savePromises);

          // Wait a moment for UI to update
          await new Promise(resolve => setTimeout(resolve, 200));

          // Re-submit
          const form = button.closest('form');
          if (form) {
            form.submit();
          } else {
            window.location.href = '/checkout';
          }
        } catch (error) {
          console.error('Error saving gift messages before checkout:', error);
          alert('Please save your gift messages before proceeding to checkout.');
        }
      }
    });
  });
});
